import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToasts } from '../contexts/ToastContext';
import axiosClient from '../api/axiosClient';

/**
 * Custom hook for creating a new log report
 * Handles geolocation retrieval and API call to create the log report
 * @returns {Object} { createLogReport, isLoading }
 */
export const useCreateLogReport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToasts();

  const submitLogReport = async (isNonClinicalCase, latitude, longitude) => {
    try {
      const response = await axiosClient.post(`log-reports/create`, {
        latitude,
        longitude,
        isNonClinicalCase
      });

      if (isNonClinicalCase) {
        navigate(`/log-reports/${response?.data?.logReport?.id}/reading-day`);
      } else {
        navigate(`/log-reports/${response?.data?.logReport?.id}/case-logs/${response?.data?.caseLog?.id}`);
      }
    } catch (error) {
      showToast(error.message, 'error');
      setIsLoading(false);
    }
  };

  const createLogReport = (isNonClinicalCase) => {
    setIsLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          submitLogReport(isNonClinicalCase, latitude, longitude);
        },
        (err) => {
          switch (err.code) {
            case err.PERMISSION_DENIED:
              showToast('User denied the request for Geolocation.', 'error');
              break;
            case err.POSITION_UNAVAILABLE:
              showToast('Location information is unavailable.', 'error');
              break;
            case err.TIMEOUT:
              showToast('The request to get user location timed out.', 'error');
              break;
            default:
              showToast(`Geolocation error: ${err.message}`, 'error');
          }
          submitLogReport(isNonClinicalCase, null, null);
        },
        { timeout: 10000 }
      );
    } else {
      showToast('Geolocation is not supported by this browser.', 'error');
      submitLogReport(isNonClinicalCase, null, null);
    }
  };

  return { createLogReport, isLoading };
};
